import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { Badge, Loading, Alert, Button, Input, Modal } from '../components/ui';

// MTM-staff admin panel. Cross-tenant visibility + manual entitlement control.
// Gated by is_admin (server enforces it too via /api/admin middleware).
interface Account {
  id: string;
  name: string;
  ownerEmail: string | null;
  plan: string;
  createdAt: string;
  onboarded: boolean;
  memberCount: number;
  contactCount: number;
  entitlements: string[];
}

export default function Admin() {
  const { me } = useAuth();
  const { data, loading, error, reload } = useAsync(
    () => apiGet<{ tools: { id: string; name: string }[]; accounts: Account[] }>('/api/admin/accounts'),
    []
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [resetTarget, setResetTarget] = useState<Account | null>(null);

  // Client guard (the server also enforces admin on every /api/admin route).
  if (me && !me.isAdmin) return <Navigate to="/" replace />;

  async function toggle(groupId: string, toolId: string, owned: boolean) {
    const cell = `${groupId}:${toolId}`;
    setBusy(cell);
    try {
      await apiPost('/api/admin/entitlements', { groupId, toolId, action: owned ? 'revoke' : 'grant' });
      await reload();
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Loading label="Loading accounts…" />;
  if (error) return <Alert>{error}</Alert>;
  const tools = data?.tools || [];
  const accounts = (data?.accounts || []).filter(
    (a) => !q || a.name.toLowerCase().includes(q.toLowerCase()) || (a.ownerEmail || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="h-display text-3xl text-soft">Admin</h1>
          <p className="mt-1 text-sm text-muted">Every account, and manual tool access. Grants here bypass Stripe.</p>
        </div>
        <Badge className="border-victory/40 text-victory">MTM staff</Badge>
      </div>

      <input className="input max-w-sm" placeholder="Search by workspace or owner email…" value={q} onChange={(e) => setQ(e.target.value)} />

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-3 py-3 font-semibold">Signed up</th>
              {tools.map((t) => <th key={t.id} className="px-3 py-3 text-center font-semibold">{t.name}</th>)}
              <th className="px-3 py-3 text-center font-semibold">Data</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className="border-b border-line/60">
                <td className="px-4 py-3">
                  <div className="font-head font-semibold text-soft">{a.name}</div>
                  <div className="text-xs text-muted">{a.ownerEmail || '—'} · {a.memberCount} {a.memberCount === 1 ? 'member' : 'members'} · {a.contactCount ?? 0} contacts{!a.onboarded && ' · onboarding'}</div>
                </td>
                <td className="px-3 py-3 text-xs text-muted">{(a.createdAt || '').slice(0, 10)}</td>
                {tools.map((t) => {
                  const owned = a.entitlements.includes(t.id);
                  const cell = `${a.id}:${t.id}`;
                  return (
                    <td key={t.id} className="px-3 py-3 text-center">
                      <button
                        onClick={() => toggle(a.id, t.id, owned)}
                        disabled={busy === cell}
                        title={owned ? 'Click to revoke' : 'Click to grant'}
                        className={`inline-flex h-7 w-12 items-center rounded-full border px-1 transition disabled:opacity-40 ${
                          owned ? 'justify-end border-success/50 bg-success/20' : 'justify-start border-line bg-surface-3'
                        }`}
                      >
                        <span className={`h-5 w-5 rounded-full ${owned ? 'bg-success' : 'bg-muted'}`} />
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-center">
                  <button onClick={() => setResetTarget(a)} className="text-xs font-semibold text-danger hover:underline">Reset</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {accounts.length === 0 && <p className="text-sm text-muted">No accounts match.</p>}

      <ResetModal account={resetTarget} onClose={() => setResetTarget(null)} onDone={() => { setResetTarget(null); reload(); }} />
    </div>
  );
}

function ResetModal({ account, onClose, onDone }: { account: Account | null; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  if (!account) return null;

  async function reset() {
    setBusy(true);
    setError(null);
    try {
      const r = await apiPost<{ cleared: Record<string, number> }>('/api/admin/reset', { groupId: account!.id, confirmName: name });
      const total = Object.values(r.cleared).reduce((a, b) => a + b, 0);
      setDone(`Cleared ${total} rows.`);
      setTimeout(onDone, 900);
    } catch (e: any) {
      setError(e?.message || 'Reset failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Reset workspace data">
      <div className="space-y-3">
        <Alert kind="error">
          This permanently deletes all contacts, companies, pipeline cards, activity, and tasks for
          <span className="font-semibold"> {account.name}</span>. Settings, team, stages, and tool access are kept.
        </Alert>
        <p className="text-sm text-muted">Type the workspace name <span className="font-semibold text-soft">{account.name}</span> to confirm.</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={account.name} />
        {error && <Alert>{error}</Alert>}
        {done && <Alert kind="success">{done}</Alert>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={busy} disabled={name.trim() !== account.name.trim()} onClick={reset}>Reset data</Button>
        </div>
      </div>
    </Modal>
  );
}
